import React, { useEffect, useState } from 'react'
import {
  Btn,
  Cnpj,
  CustomDropdown,
  CustomInputTextArea,
  CustomModal,
  DateTime,
  InputNumeroInteiro,
  InputPhone,
  NumeroDecimal,
  SectionTitle,
} from '../../../../../components'
import { getEmissoresByTps } from '../../../../../services/api/https/settings/emissor-cte.http'
import { getCfops } from '../../../../../services/api/https/settings/cfop.http'
import { postEmitCteSubcontratacao } from '../../../../../services/api/https/deliveries/cte.http'
import { normalizarCnpj, somenteDigitos } from '../../../../../services/util/documento.service'
import ValoresFiscaisCte from '../ValoresFiscaisCte/ValoresFiscaisCte'
import useEmissorUnico from '../Partes/useEmissorUnico'
import {
  ERROS_VALORES_FISCAIS,
  VALORES_FISCAIS_INICIAIS,
  erroCfop,
  erroNumeroOrdemColeta,
  erroValoresFiscais,
  filtrarCfops,
  montarComponentesValor,
} from '../ValoresFiscaisCte/valoresFiscaisCte.util'
import {
  cidadeUf,
  descreverDocumentosOrigem,
  descreverQuantidadesOrigem,
  formatarChaveCte,
} from './cteSubcontratacao.util'

const inicialState = {
  emissor_id: null,
  cfop_id: null,
  numero_ordem_coleta: null,
  ...VALORES_FISCAIS_INICIAIS,
  observacoes: null,
}

const errors = {
  emissor_id: 'O campo Emissor é obrigatório',
  cfop_id: 'O campo CFOP é obrigatório',
  numero_ordem_coleta: 'O campo Nº Ordem Coleta é obrigatório',
  ...ERROS_VALORES_FISCAIS,
}

const observacaoPadrao = cteOrigem =>
  `CT-e de subcontratação referente ao CT-e nº ${cteOrigem.numero} série ${cteOrigem.serie} (chave ${cteOrigem.chave}), emitido por ${cteOrigem.emitente_nome}.`

export default function ModalEmitirCteSubcontratacao({
  selectedTp,
  visible,
  cteOrigem,
  onModalClose,
  onModalSave,
  onError,
}) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [state, setState] = useState(inicialState)
  const [componentes, setComponentes] = useState([])
  const [emissores, setEmissores] = useState({ emissores: [], selected: null })
  const [cfops, setCfops] = useState({ cfops: [], selected: null })
  // Telefone do tomador (emitente de origem): exigido pela Focus e nem sempre presente no XML
  const [telefoneTomador, setTelefoneTomador] = useState('')

  useEffect(() => {
    if (!visible) return
    setState({ ...inicialState, observacoes: observacaoPadrao(cteOrigem) })
    setComponentes([])
    setEmissores(prevState => ({ ...prevState, selected: null }))
    setCfops(prevState => ({ ...prevState, selected: null }))
    setTelefoneTomador(cteOrigem.emitente_telefone || '')
  }, [visible, cteOrigem])

  useEffect(() => {
    async function fetchData() {
      try {
        const [listaEmissores, listaCfops] = await Promise.all([
          getEmissoresByTps(selectedTp),
          getCfops(),
        ])
        setEmissores({ emissores: listaEmissores, selected: null })
        setCfops({
          cfops: listaCfops.map(cfop => ({
            ...cfop,
            codigoNatureza: `${cfop.codigo} - ${cfop.natureza}`,
          })),
          selected: null,
        })
      } catch (error) {
        onError(error)
      }
    }
    if (selectedTp) fetchData()
  }, [selectedTp])

  useEmissorUnico({
    visible,
    emissores: emissores.emissores,
    selecionado: emissores.selected,
    onSelecionar: emissor => {
      setState(prevState => ({ ...prevState, emissor_id: emissor.id }))
      setEmissores(prevState => ({ ...prevState, selected: emissor }))
    },
  })

  const ufEmissor = emissores.selected ? emissores.selected.uf : null
  const ufInicioOrigem = cteOrigem ? cteOrigem.uf_inicio : null
  const ufFimOrigem = cteOrigem ? cteOrigem.uf_fim : null
  const cfopsFiltrados = filtrarCfops(cfops.cfops, ufEmissor, ufInicioOrigem, ufFimOrigem)
  const cfopSelecionadoId = cfops.selected ? cfops.selected.id : null

  useEffect(() => {
    if (cfopSelecionadoId && !cfopsFiltrados.some(cfop => cfop.id === cfopSelecionadoId)) {
      setState(prevState => ({ ...prevState, cfop_id: null }))
      setCfops(prevState => ({ ...prevState, selected: null }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfopSelecionadoId, ufEmissor, ufInicioOrigem, ufFimOrigem])

  // Validação (espelho das regras do backend; a primeira inconsistência é devolvida)
  const validar = () => {
    if (!selectedTp) return 'Selecione o Operador Logístico'
    for (const key of Object.keys(state)) {
      if (key !== 'observacoes' && state[key] === null) return errors[key]
    }
    const erroOrdemColeta = erroNumeroOrdemColeta(state.numero_ordem_coleta)
    if (erroOrdemColeta) return erroOrdemColeta
    if (emissores.selected && normalizarCnpj(emissores.selected.cnpj) === cteOrigem.emitente_cnpj) {
      return 'O Emissor é o emitente do CTe de origem: a subcontratação deve ser emitida pela transportadora subcontratada'
    }
    const telefone = somenteDigitos(telefoneTomador)
    if (telefone.length < 6 || telefone.length > 14) {
      return 'Informe o Telefone do Tomador (emitente do CTe de origem) com 6 a 14 dígitos'
    }
    if (!cfops.selected) return 'Selecione o CFOP'
    const erroDoCfop = erroCfop(cfops.selected.codigo, ufEmissor, ufInicioOrigem, ufFimOrigem)
    if (erroDoCfop) return erroDoCfop
    const erroFiscal = erroValoresFiscais(state)
    if (erroFiscal) return erroFiscal
    if (state.observacoes && state.observacoes.length > 2000) {
      return 'O campo Observações deve ter no máximo 2000 caracteres'
    }
    return null
  }

  // Events
  const onBtnSalvarClicked = async () => {
    try {
      const erro = validar()
      if (erro) throw new Error(erro)

      setIsLoading(true)

      const componentesValor = montarComponentesValor(componentes)

      await postEmitCteSubcontratacao(selectedTp, {
        ...state,
        cte_origem_id: cteOrigem.id,
        telefone_tomador: somenteDigitos(telefoneTomador),
        ...(componentesValor.length ? { componentes_valor: componentesValor } : {}),
      })

      onModalSave()
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const renderCampo = (label, valor) => (
    <div style={{ fontSize: 13, paddingTop: 2, paddingBottom: 2 }}>
      <b>{label}:</b> {valor}
    </div>
  )

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header={'Emitir CTe de Subcontratação'}
      width={900}
      onClose={onModalClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <form className="p-grid" onSubmit={e => e.preventDefault()}>
        {/* CTe de origem (somente leitura): identificação e partes à esquerda, prestação e carga à direita */}
        <div className="p-col-12">
          <SectionTitle title="CTe de Origem" />
        </div>
        <div className="p-col-12">
          <div
            className="p-grid"
            style={{
              backgroundColor: '#e3f2fd',
              padding: '8px 10px',
              borderRadius: '5px',
              margin: 0,
            }}
          >
            <div className="p-md-6 p-col-12">
              {renderCampo('Chave', formatarChaveCte(cteOrigem.chave))}
              {renderCampo(
                'CTe',
                `nº ${cteOrigem.numero} · série ${cteOrigem.serie} · modelo ${cteOrigem.modelo}`,
              )}
              {renderCampo('Emissão', <DateTime>{cteOrigem.data_emissao}</DateTime>)}
              {renderCampo(
                'Emitente (tomador)',
                <>
                  {cteOrigem.emitente_nome} · <Cnpj>{cteOrigem.emitente_cnpj}</Cnpj> · IE{' '}
                  {cteOrigem.emitente_inscricao_estadual || '-'} · {cteOrigem.emitente_uf}
                </>,
              )}
              {renderCampo(
                'Remetente',
                <>
                  {cteOrigem.remetente_nome} · <Cnpj>{cteOrigem.remetente_cnpj_cpf}</Cnpj>
                </>,
              )}
              {renderCampo(
                'Destinatário',
                <>
                  {cteOrigem.destinatario_nome} · <Cnpj>{cteOrigem.destinatario_cnpj_cpf}</Cnpj>
                </>,
              )}
              {renderCampo('CFOP de origem', `${cteOrigem.cfop} - ${cteOrigem.natureza_operacao}`)}
            </div>
            <div className="p-md-6 p-col-12">
              {renderCampo('Origem', cidadeUf(cteOrigem.municipio_inicio, cteOrigem.uf_inicio))}
              {renderCampo('Destino', cidadeUf(cteOrigem.municipio_fim, cteOrigem.uf_fim))}
              {renderCampo('Produto predominante', cteOrigem.produto_predominante)}
              {renderCampo('Quantidades', descreverQuantidadesOrigem(cteOrigem.quantidades_json))}
              {renderCampo(
                'Valor da carga',
                <>
                  R$ <NumeroDecimal>{cteOrigem.valor_carga}</NumeroDecimal>
                </>,
              )}
              {renderCampo(
                'Frete do CTe de origem',
                <>
                  R$ <NumeroDecimal>{cteOrigem.valor_prestacao}</NumeroDecimal>
                </>,
              )}
              {renderCampo('Documentos', descreverDocumentosOrigem(cteOrigem.documentos_json))}
            </div>
          </div>
        </div>

        {/* Dados da emissão */}
        <div className="p-col-12">
          <SectionTitle title="Dados da Emissão" />
        </div>
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="razao_social"
            options={emissores.emissores}
            value={emissores.selected}
            label="Emissor:*"
            filter={true}
            onChange={value => {
              setState(prevState => ({ ...prevState, emissor_id: value.id }))
              setEmissores(prevState => ({ ...prevState, selected: value }))
            }}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <InputNumeroInteiro
            label="Nº Ordem Coleta:*"
            marginTop={10}
            value={state.numero_ordem_coleta}
            onChange={value => setState({ ...state, numero_ordem_coleta: value })}
          />
          <span style={{ color: 'gray', fontSize: 12 }}>
            Número gerado pela transportadora associado a ordem de coleta
          </span>
        </div>
        <div className="p-md-8 p-col-12">
          <CustomDropdown
            optionLabel="codigoNatureza"
            options={cfopsFiltrados}
            value={cfops.selected}
            label="CFOP:*"
            filter={true}
            onChange={value => {
              setState(prevState => ({ ...prevState, cfop_id: value.id }))
              setCfops(prevState => ({ ...prevState, selected: value }))
            }}
            tooltip={
              'Lista filtrada pela UF do emissor e pelas UFs de início e fim do CTe de origem (matriz da SEFAZ)'
            }
          />
        </div>
        <div className="p-md-4 p-col-12">
          <InputPhone
            label="Telefone do tomador:*"
            marginTop={10}
            mask="(99)9999-9999?9"
            autoClear={false}
            value={telefoneTomador}
            onChange={value => setTelefoneTomador(value || '')}
          />
          <span style={{ color: 'gray', fontSize: 12 }}>
            Emitente do CTe de origem; vem do XML quando informado
          </span>
        </div>

        <ValoresFiscaisCte
          key={visible ? 'aberto' : 'fechado'}
          state={state}
          onStateChange={setState}
          componentes={componentes}
          onComponentesChange={setComponentes}
          onError={onError}
        />

        <div className="p-col-12">
          <CustomInputTextArea
            label="Observações"
            marginTop={10}
            rows={4}
            value={state.observacoes || ''}
            onChange={value => setState({ ...state, observacoes: value })}
          />
        </div>

        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnSalvarClicked} isLoading={isLoading}>
            Salvar
          </Btn>
        </div>
      </form>
    </CustomModal>
  )
}
